import {WinPresentationController} from "../../WinPresentationController";
import {gsap, TimelineLite} from "gsap";
import {SlotGame} from "@nolimitcity/slot-game/bin/SlotGame";
import {RockBottom} from "../../../RockBottom";
import {RockBottomGameConfig} from "../../../RockBottomGameConfig";
import {IBetWayWinData} from "../../../server/data/IRockBottomData";
import {RockBottomSymbolState} from "../../../symbol/RockBottomSymbolConfig";
import {BelowStakeWinRestriction} from "../../../server/BelowStakeWinRestriction";
import {RockBottomSound} from "../../../sound/RockBottomSound";
import {RockBottomGameMode} from "../../../server/RockBottomGameMode";

export class InitialWinController extends WinPresentationController {

    private _mainTimeline: TimelineLite | null;

    private _belowStakeWinRestriction: boolean;
    private _winSoundName: string = "";


    constructor() {
        super("InitialWinController", true);
    }

    protected hasWin(): boolean {
        return RockBottom.model.parsedGameData.betWins?.length > 0;
    }

    protected async createWinAnimation(): Promise<any> {
        this._belowStakeWinRestriction = BelowStakeWinRestriction.isWinBelowPlayedBetValue();
        const tl = gsap.timeline();
        if (RockBottom.inBonusGame) {
            tl.add([
                this.getWinAnimation(),
                RockBottom.initialWinMult.updateProps()
            ]);
            tl.add([
                this.getWinCountUpAnimation(),
                () => {
                    RockBottom.initialWinMult.remove()
                }
            ], 1.8); // time to overlap fading out of countup on sym
        } else {
            tl.add([
                this.getWinAnimation(),
                this.getWinCountUpAnimation()
            ]);
        }

        this._mainTimeline = tl;
        await Promise.resolve(tl);
        this._mainTimeline = null;
    }

    protected getWinAnimation(): TimelineLite {
        const winRatio = RockBottom.model.parsedGameData.currentWinRatioConfig?.ratio!;
        const tl = gsap.timeline();
        let winDelay = 0;
        if (!this._belowStakeWinRestriction) {
            // TODO: play sound.
        }
        RockBottom.model.parsedGameData.betWins.forEach(w => {
            tl.add(this.getSingleBetWayWinAnimation(<IBetWayWinData>w, winRatio), winDelay);
            //#35 long delay removed
            //winDelay += (winRatio < RockBottomGameConfig.WIN_RATIOS[3].ratio ? 0 : tl.totalDuration() + 0.2);
        });
        return tl;
    }

    protected getSingleBetWayWinAnimation(winData: IBetWayWinData, winRatio: number): TimelineLite {
        const tl = gsap.timeline();
        let symbolDelay = 0;
        const reelDelay = this.getReelDelay(winRatio);
        winData.reelWinPositions.forEach((reelWin, reelId) => {
            let hasReelWin = false;
            reelWin.forEach((winPosition, symId) => {
                if (winPosition) {
                    hasReelWin = true;
                    tl.add(this.getSymbolWinAnimation(reelId, symId, winRatio), symbolDelay);
                }
            });
            if (hasReelWin) {
                symbolDelay += reelDelay;
            }
        });

        // TODO: add WinWays animation.
        return tl;
    }

    protected getSymbolWinAnimation(reelId: number, symId: number, winRatio: number): TimelineLite {
        const tl: TimelineLite = new TimelineLite(),
            sym = RockBottom.reels[reelId].getSymbol(symId, false, false);

        tl.add(() => {
            RockBottom.reels[reelId].reelView.addWinSymbol(sym);
            RockBottom.midSymExpand.playWinAnimation(reelId, symId, sym.symbolName);
        });
        tl.add(sym.getStateAnimation(RockBottomSymbolState.WIN));

        return tl;
    }

    protected getWinCountUpAnimation(): TimelineLite {
        const data = RockBottom.model.parsedGameData;
        const tl = gsap.timeline();
        if (data.currentWinRatioConfig!.bigWinLevel > -1 && (data.mode !== RockBottomGameMode.WINCAP_PICK)) {
            RockBottomSound.fadeMusic(0, 1);
            RockBottomSound.soundPlayer.stopTrack(RockBottomSound.PRE_BIGWIN_ANTICIPATION);
            RockBottomSound.playTrack(RockBottomSound.PRE_BIG_WIN);
            RockBottom.njReqWinView.showWinAndWays(0.5);
            return tl; // no count up for big win.
        }

        const duration: number = data.currentWinRatioConfig!.duration;
        if (!this._belowStakeWinRestriction) {
            tl.add(() => {
                const winSoundName: string | undefined = data.currentWinRatioConfig?.soundName;
                if (winSoundName) {
                    this._winSoundName = "";
                    this._winSoundName = winSoundName;
                    RockBottomSound.playTrack(winSoundName);
                }
            });
            tl.add([
                RockBottom.initialCountUp.getCountUpAnimation(0, data.singleWin, duration, data.currentMultiplier, data.totalWaysWin),
                RockBottom.njReqWinView.showWinAndWays(0.5)
            ]);
            tl.add(() => {
                if (!(data.currentWinRatioConfig!.bigWinLevel > -1) && data.currentWinRatioConfig?.endSoundName) {
                    RockBottomSound.playTrack(data.currentWinRatioConfig?.endSoundName);
                }
            }, "-=0.3");
        }
        if (data.mode !== RockBottomGameMode.WINCAP_PICK) {
            tl.add(() => {
                const showTotalWin: boolean = RockBottom.inBonusGame || data.nextMode === RockBottomGameMode.FREESPIN || data.nextMode === RockBottomGameMode.SUPER_FREESPIN;
                SlotGame.keypadPlugin.setWin(data.totalWin, showTotalWin, BelowStakeWinRestriction.isWinBelowStake());
            });
        }
        return tl;
    }

    protected skip() {
        this._mainTimeline?.progress(1);
    }

    private getReelDelay(winRatio: number): number {
        // tiny/small
        if (winRatio < RockBottomGameConfig.WIN_RATIOS[2].ratio) {
            return 0;
        }

        //mid
        if (winRatio < RockBottomGameConfig.WIN_RATIOS[3].ratio) {
            return 0.05;
        }

        // large and up
        return 0.1;
    }
}